import {
    EMPTY_FILTER_SELECTION,
    FilterCounts,
    FilterDefinition,
    FilterItemValues,
    FilterKey,
    FilterOption,
    FilterSelection,
    FilterValueId,
} from "../types/filters";

/** The filters that have at least one value picked. */
export function activeFilterKeys(selection: FilterSelection): FilterKey[] {
    return Object.keys(selection.values).filter(key => selection.values[key]?.length > 0);
}

export function hasActiveFilters(selection: FilterSelection): boolean {
    return activeFilterKeys(selection).length > 0;
}

/**
 * True when the item has one of the picked values of every active filter. `exceptKey` leaves one
 * filter out, which is how a menu counts its own values.
 */
export function passesFilters(item: FilterItemValues, selection: FilterSelection, exceptKey?: FilterKey): boolean {
    for (const key of activeFilterKeys(selection)) {
        if (key === exceptKey) {
            continue;
        }
        const picked = selection.values[key];
        const has = item[key];
        if (!has || !has.some(value => picked.includes(value))) {
            return false;
        }
    }
    return true;
}

/**
 * For each of `keys`, how many items each value would leave: the item must pass every other
 * filter, and its own value counts whatever is picked for that key.
 */
export function countFilterValues(items: FilterItemValues[], selection: FilterSelection, keys: FilterKey[]): FilterCounts {
    const counts: FilterCounts = {};
    keys.forEach(key => counts[key] = {});
    const active = activeFilterKeys(selection);
    for (const item of items) {
        let failing: FilterKey | undefined;
        let failures = 0;
        for (const key of active) {
            const picked = selection.values[key];
            const has = item[key];
            if (!has || !has.some(value => picked.includes(value))) {
                failing = key;
                failures++;
                if (failures > 1) {
                    break;
                }
            }
        }
        if (failures > 1) {
            continue;
        }
        for (const key of keys) {
            if (failures === 1 && failing !== key) {
                continue;
            }
            const bucket = counts[key];
            for (const value of Array.from(new Set(item[key] ?? []))) {
                bucket[value] = (bucket[value] ?? 0) + 1;
            }
        }
    }
    return counts;
}

/**
 * The selection a page applies: values that no longer exist (a deleted team, a skill nobody has)
 * are dropped, but only for filters whose options are known; an empty option list means "not loaded yet".
 */
export function effectiveSelection(selection: FilterSelection, definitions: FilterDefinition[]): FilterSelection {
    const values: Record<FilterKey, FilterValueId[]> = {};
    for (const key of activeFilterKeys(selection)) {
        const definition = definitions.find(d => d.key === key);
        if (definition && definition.options.length > 0) {
            const known = new Set(definition.options.map(option => option.id));
            const kept = selection.values[key].filter(value => known.has(value));
            if (kept.length > 0) {
                values[key] = kept;
            }
        } else {
            values[key] = selection.values[key];
        }
    }
    return { values, added: selection.added };
}

export function toggleFilterValue(selection: FilterSelection, key: FilterKey, value: FilterValueId): FilterSelection {
    const current = selection.values[key] ?? [];
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    return { ...selection, values: { ...selection.values, [key]: next } };
}

export function onlyFilterValue(selection: FilterSelection, key: FilterKey, value: FilterValueId): FilterSelection {
    return { ...selection, values: { ...selection.values, [key]: [value] } };
}

export function clearFilter(selection: FilterSelection, key: FilterKey): FilterSelection {
    const values = { ...selection.values };
    delete values[key];
    return { ...selection, values };
}

export function addFilter(selection: FilterSelection, key: FilterKey): FilterSelection {
    return selection.added.includes(key) ? selection : { ...selection, added: [...selection.added, key] };
}

/** Takes the filter off the bar: its values go and its chip with them. */
export function removeFilter(selection: FilterSelection, key: FilterKey): FilterSelection {
    const cleared = clearFilter(selection, key);
    return { ...cleared, added: cleared.added.filter(k => k !== key) };
}

/** Clear keeps the filters someone added, empty, so the chips they use stay at hand. */
export function clearFilters(selection: FilterSelection): FilterSelection {
    return { ...EMPTY_FILTER_SELECTION, added: selection.added };
}

/** "North", "North, South", "North +2": the picked values as a chip says them. */
export function summarizeFilterValues(values: FilterValueId[], options: FilterOption[]): string {
    const labels = values
        .map(value => options.find(option => option.id === value)?.label)
        .filter((label): label is string => !!label);
    if (labels.length <= 2) {
        return labels.join(", ");
    }
    return `${labels[0]} +${labels.length - 1}`;
}

export function sameSelection(a: FilterSelection, b: FilterSelection): boolean {
    const keys = Array.from(new Set([...activeFilterKeys(a), ...activeFilterKeys(b)]));
    for (const key of keys) {
        const av = a.values[key] ?? [];
        const bv = b.values[key] ?? [];
        if (av.length !== bv.length || av.some(v => !bv.includes(v))) {
            return false;
        }
    }
    return a.added.length === b.added.length && a.added.every(k => b.added.includes(k));
}
